import type { EnumFormatConfig } from './column-format-options';
import {
  tasStateLabelKeyMap,
  tasStateLabelMap,
} from 'tio-data/data/resources/tas-application.constants';

// Re-export for consumers that need the map directly
export { tasStateLabelKeyMap, tasStateLabelMap };

// Program instance labels use descriptive forms to avoid ambiguity in flat
// report/filter contexts where multiple states map to "Pending" or "Approved".
export const TAS_PROGRAM_INSTANCE_STATE_LABELS: Record<string, string> = {
  'TAS.ProgramInstanceState.CREATED': 'Created',
  'TAS.ProgramInstanceState.DRAFT': 'In Draft',
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': 'Pending Program Approval',
  'TAS.ProgramInstanceState.PROGRAM_APPROVED': 'Program Approved',
  'TAS.ProgramInstanceState.PROGRAM_REJECTED': 'Program Rejected',
  'TAS.ProgramInstanceState.PROGRAM_NOT_APPROVED': 'Program Not Approved',
  'TAS.ProgramInstanceState.PRIMED': 'In Progress',
  'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': 'Pending Completion Approval',
  'TAS.ProgramInstanceState.COMPLETION_APPROVED': 'Completion Approved',
  'TAS.ProgramInstanceState.COMPLETION_REJECTED': 'Completion Rejected',
  'TAS.ProgramInstanceState.COMPLETION_NOT_APPROVED': 'Completion Not Approved',
  'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': 'Pending Withdrawal Approval',
  'TAS.ProgramInstanceState.WITHDRAWAL_APPROVED': 'Withdrawal Approved',
  'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': 'Withdrawal Rejected',
  'TAS.ProgramInstanceState.WITHDRAWAL_NOT_APPROVED': 'Withdrawal Not Approved',
  'TAS.ProgramInstanceState.COMPLETED': 'Completed',
  'TAS.ProgramInstanceState.WITHDRAWN': 'Withdrawn',
  'TAS.ProgramInstanceState.ABANDONED': 'Canceled',
};

export const TAS_PROGRAM_INSTANCE_STATE_LABEL_KEYS: Record<string, string> = {
  'TAS.ProgramInstanceState.CREATED': 'common.tas.state_badge.created',
  'TAS.ProgramInstanceState.DRAFT': 'common.tas.state_badge.in_draft',
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL':
    'common.tas.state_badge.pending_program_approval',
  'TAS.ProgramInstanceState.PROGRAM_APPROVED': 'common.tas.state_badge.program_approved',
  'TAS.ProgramInstanceState.PROGRAM_REJECTED': 'common.tas.state_badge.program_rejected',
  'TAS.ProgramInstanceState.PROGRAM_NOT_APPROVED': 'common.tas.state_badge.program_not_approved',
  'TAS.ProgramInstanceState.PRIMED': 'common.tas.state_badge.in_progress',
  'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL':
    'common.tas.state_badge.pending_completion_approval',
  'TAS.ProgramInstanceState.COMPLETION_APPROVED': 'common.tas.state_badge.completion_approved',
  'TAS.ProgramInstanceState.COMPLETION_REJECTED': 'common.tas.state_badge.completion_rejected',
  'TAS.ProgramInstanceState.COMPLETION_NOT_APPROVED':
    'common.tas.state_badge.completion_not_approved',
  'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL':
    'common.tas.state_badge.pending_withdrawal_approval',
  'TAS.ProgramInstanceState.WITHDRAWAL_APPROVED': 'common.tas.state_badge.withdrawal_approved',
  'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': 'common.tas.state_badge.withdrawal_rejected',
  'TAS.ProgramInstanceState.WITHDRAWAL_NOT_APPROVED':
    'common.tas.state_badge.withdrawal_not_approved',
  'TAS.ProgramInstanceState.COMPLETED': 'common.tas.state_badge.completed',
  'TAS.ProgramInstanceState.WITHDRAWN': 'common.tas.state_badge.withdrawn',
  'TAS.ProgramInstanceState.ABANDONED': 'common.tas.state_badge.canceled',
};

// TAS audit datasets (audit_queue worklist + audit_log measurements).
const AUDIT_DECISION_ACTION_LABELS: Record<string, string> = {
  approve_evidence: 'Approve evidence',
  reject_evidence: 'Reject evidence',
  decline_evidence: 'Decline evidence',
};

export const AUDIT_REASON_LABELS: Record<string, string> = {
  manual: 'Manual',
  high_amount: 'High amount',
  random_sample: 'Random sample',
  eval_disagreement: 'Eval disagreement',
  prior_audit: 'Prior audit',
  elevated_approver_sample: 'Elevated approver',
};

const AUDIT_OUTCOME_LABELS: Record<string, string> = {
  agreed: 'Agreed',
  corrected: 'Corrected',
};

const AUDIT_STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  escalated: 'Escalated',
  closed: 'Closed',
  superseded: 'Superseded',
};

const AUDIT_FLAG_LABELS: Record<string, string> = {
  pending: 'Pending',
  audit: 'Flagged for audit',
  no_audit_needed: 'Not sampled',
  excluded: 'Excluded',
};

// Covers both decided_by (system/person) and audited_group (ai/human) tokens.
export const AUDIT_DECIDER_LABELS: Record<string, string> = {
  system: 'AI',
  person: 'Human',
  ai: 'AI',
  human: 'Human',
};

const AUDIT_DECISION_ACTION_LABEL_KEYS: Record<string, string> = {
  approve_evidence: 'common.tas.audit_label.action.approve_evidence',
  reject_evidence: 'common.tas.audit_label.action.reject_evidence',
  decline_evidence: 'common.tas.audit_label.action.decline_evidence',
};

export const AUDIT_REASON_LABEL_KEYS: Record<string, string> = {
  manual: 'common.tas.audit_label.reason.manual',
  high_amount: 'common.tas.audit_label.reason.high_amount',
  random_sample: 'common.tas.audit_label.reason.random_sample',
  eval_disagreement: 'common.tas.audit_label.reason.eval_disagreement',
  prior_audit: 'common.tas.audit_label.reason.prior_audit',
  elevated_approver_sample: 'common.tas.audit_label.reason.elevated_approver_sample',
};

const AUDIT_OUTCOME_LABEL_KEYS: Record<string, string> = {
  agreed: 'common.tas.audit_label.outcome.agreed',
  corrected: 'common.tas.audit_label.outcome.corrected',
};

const AUDIT_STATUS_LABEL_KEYS: Record<string, string> = {
  open: 'common.tas.audit_label.status.open',
  escalated: 'common.tas.audit_label.status.escalated',
  closed: 'common.tas.audit_label.status.closed',
  superseded: 'common.tas.audit_label.status.superseded',
};

const AUDIT_FLAG_LABEL_KEYS: Record<string, string> = {
  pending: 'common.tas.audit_label.flag.pending',
  audit: 'common.tas.audit_label.flag.audit',
  no_audit_needed: 'common.tas.audit_label.flag.no_audit_needed',
  excluded: 'common.tas.audit_label.flag.excluded',
};

export const AUDIT_DECIDER_LABEL_KEYS: Record<string, string> = {
  system: 'common.tas.audit_label.decider.ai',
  person: 'common.tas.audit_label.decider.human',
  ai: 'common.tas.audit_label.decider.ai',
  human: 'common.tas.audit_label.decider.human',
};

interface EnumLabelGroup {
  labels: Record<string, string>;
  keys: Record<string, string>;
}

const TAS_STATE: EnumLabelGroup = { labels: tasStateLabelMap, keys: tasStateLabelKeyMap };
const TAS_PROGRAM_INSTANCE_STATE: EnumLabelGroup = {
  labels: TAS_PROGRAM_INSTANCE_STATE_LABELS,
  keys: TAS_PROGRAM_INSTANCE_STATE_LABEL_KEYS,
};
const AUDIT_DECISION_ACTION: EnumLabelGroup = {
  labels: AUDIT_DECISION_ACTION_LABELS,
  keys: AUDIT_DECISION_ACTION_LABEL_KEYS,
};
const AUDIT_REASON: EnumLabelGroup = { labels: AUDIT_REASON_LABELS, keys: AUDIT_REASON_LABEL_KEYS };
const AUDIT_OUTCOME: EnumLabelGroup = {
  labels: AUDIT_OUTCOME_LABELS,
  keys: AUDIT_OUTCOME_LABEL_KEYS,
};
const AUDIT_STATUS: EnumLabelGroup = { labels: AUDIT_STATUS_LABELS, keys: AUDIT_STATUS_LABEL_KEYS };
const AUDIT_FLAG: EnumLabelGroup = { labels: AUDIT_FLAG_LABELS, keys: AUDIT_FLAG_LABEL_KEYS };
const AUDIT_DECIDER: EnumLabelGroup = {
  labels: AUDIT_DECIDER_LABELS,
  keys: AUDIT_DECIDER_LABEL_KEYS,
};

const ENUM_FIELD_REGISTRY: Record<string, EnumLabelGroup> = {
  // snake_case (fallback fields / OS source paths)
  tas_application_state_keyword: TAS_STATE,
  application_state: TAS_STATE,
  tas_application_payment_state_keyword: TAS_STATE,
  tas_program_instance_state_keyword: TAS_PROGRAM_INSTANCE_STATE,
  action_taken_keyword: AUDIT_DECISION_ACTION,
  correct_action_keyword: AUDIT_DECISION_ACTION,
  audit_reason_keyword: AUDIT_REASON,
  audit_outcome_keyword: AUDIT_OUTCOME,
  audit_status_keyword: AUDIT_STATUS,
  audit_flag_keyword: AUDIT_FLAG,
  decided_by_keyword: AUDIT_DECIDER,
  audited_group_keyword: AUDIT_DECIDER,
  // camelCase (/fields API response)
  tasApplicationStateKeyword: TAS_STATE,
  applicationState: TAS_STATE,
  tasApplicationPaymentStateKeyword: TAS_STATE,
  tasProgramInstanceStateKeyword: TAS_PROGRAM_INSTANCE_STATE,
  actionTakenKeyword: AUDIT_DECISION_ACTION,
  correctActionKeyword: AUDIT_DECISION_ACTION,
  auditReasonKeyword: AUDIT_REASON,
  auditOutcomeKeyword: AUDIT_OUTCOME,
  auditStatusKeyword: AUDIT_STATUS,
  auditFlagKeyword: AUDIT_FLAG,
  decidedByKeyword: AUDIT_DECIDER,
  auditedGroupKeyword: AUDIT_DECIDER,
};

export function getEnumFormatForField(fieldKey: string): EnumFormatConfig | undefined {
  const group = ENUM_FIELD_REGISTRY[fieldKey];
  if (!group) return undefined;
  return { type: 'enum', valueMap: group.labels };
}

/**
 * Like `getEnumFormatForField`, but each label is run through `translate` (typically
 * `(key) => intl.t(key)`). A raw value with no translation key keeps its English label.
 */
export function getTranslatedEnumFormatForField(
  fieldKey: string,
  translate: (key: string) => string
): EnumFormatConfig | undefined {
  const group = ENUM_FIELD_REGISTRY[fieldKey];
  if (!group) return undefined;
  const valueMap = Object.fromEntries(
    Object.entries(group.labels).map(([raw, label]) => {
      const key = group.keys[raw];
      return [raw, key ? translate(key) : label];
    })
  );
  return { type: 'enum', valueMap };
}
